import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const AD_HOC_REASONS = [
  { value: 'TEMP_TIME_CHANGE', label: 'เปลี่ยนเวลาเรียนชั่วคราว' },
  { value: 'TEMP_ADD_CLASS', label: 'เพิ่มคลาสเรียนประจำสัปดาห์ชั่วคราว' },
  { value: 'OTHER', label: 'อื่นๆ' },
];

const PERMANENT_SUB_TYPES = [
  { value: 'CHANGE_TIME', label: 'แก้ไขเวลาเรียน' },
  { value: 'CANCEL_PERMANENT', label: 'ยกเลิกตารางเรียนนี้ (ถาวร)' },
  { value: 'CHANGE_SUBJECT', label: 'แก้ไขรายวิชา' },
  { value: 'OTHER', label: 'อื่นๆ' },
];

interface EditClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classTemplateId: string;
  eventDate: string;
  teacherCode: string;
  defaultSubject?: string;
  onSuccess?: () => void;
}

function useTimeOptions() {
  return useMemo(() => {
    const opts: string[] = [];
    for (let h = 6; h <= 24; h++) {
      for (const m of [0, 30]) {
        if (h === 24 && m > 0) continue;
        opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return opts;
  }, []);
}

export const EditClassDialog: React.FC<EditClassDialogProps> = ({
  open, onOpenChange, classTemplateId, eventDate, teacherCode, defaultSubject, onSuccess,
}) => {
  const timeOptions = useTimeOptions();
  const [editCategory, setEditCategory] = useState<'AD_HOC' | 'PERMANENT' | ''>('');
  const [adHocReason, setAdHocReason] = useState('');
  const [permanentSubType, setPermanentSubType] = useState('');

  // Ad-hoc fields
  const [adHocDate, setAdHocDate] = useState<Date | undefined>(undefined);
  const [adHocStart, setAdHocStart] = useState('');
  const [adHocEnd, setAdHocEnd] = useState('');
  const [adHocSubject, setAdHocSubject] = useState('');

  // Permanent change_time fields
  const [permDate, setPermDate] = useState<Date | undefined>(undefined);
  const [permStart, setPermStart] = useState('');
  const [permEnd, setPermEnd] = useState('');

  // Permanent change_subject fields
  const [permSubject, setPermSubject] = useState('');
  const [permGrade, setPermGrade] = useState('');
  const [isSpecialTutoring, setIsSpecialTutoring] = useState(false);

  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setEditCategory('');
      setAdHocReason('');
      setPermanentSubType('');
      setAdHocDate(new Date(eventDate));
      setAdHocStart('');
      setAdHocEnd('');
      setAdHocSubject(defaultSubject || '');
      setPermDate(new Date(eventDate));
      setPermStart('');
      setPermEnd('');
      setPermSubject(defaultSubject || '');
      setPermGrade('');
      setIsSpecialTutoring(false);
      setNote('');
    }
  }, [open, eventDate, defaultSubject]);

  const handleSubmit = async () => {
    if (!editCategory) {
      toast.error('กรุณาเลือกประเภทการแก้ไข');
      return;
    }

    const payload: Record<string, any> = {};
    let editSubType = '';

    if (editCategory === 'AD_HOC') {
      if (!adHocReason) { toast.error('กรุณาเลือกเหตุผล'); return; }
      editSubType = adHocReason;
      if (adHocDate) payload.new_date = format(adHocDate, 'yyyy-MM-dd');
      if (adHocStart) payload.start_time = adHocStart;
      if (adHocEnd) payload.end_time = adHocEnd;
      if (adHocSubject) payload.subject = adHocSubject;
    } else {
      if (!permanentSubType) { toast.error('กรุณาเลือกหัวข้อการแก้ไข'); return; }
      editSubType = permanentSubType;
      if (permanentSubType === 'CHANGE_TIME') {
        if (permDate) payload.new_date = format(permDate, 'yyyy-MM-dd');
        if (permStart) payload.start_time = permStart;
        if (permEnd) payload.end_time = permEnd;
      } else if (permanentSubType === 'CHANGE_SUBJECT') {
        if (permSubject) payload.subject = permSubject;
        if (permGrade) payload.grade = permGrade;
        payload.is_special_tutoring = isSpecialTutoring;
      }
    }

    setSubmitting(true);
    try {
      const { error } = await (supabase as any)
        .from('v_class_template_events')
        .insert({
          class_template_id: classTemplateId,
          event_date: eventDate,
          event_type: 'EDIT',
          edit_category: editCategory,
          edit_sub_type: editSubType,
          edit_payload: payload,
          note: note || null,
          requested_by: teacherCode,
          status: 'PENDING',
        });
      if (error) throw error;
      onOpenChange(false);
      setShowSuccess(true);
    } catch (err: any) {
      toast.error(err.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
    }
  };

  const renderTimeSelectors = (
    startVal: string, setStartVal: (v: string) => void,
    endVal: string, setEndVal: (v: string) => void,
    dateVal: Date | undefined, setDateVal: (v: Date | undefined) => void,
  ) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <Label>วันที่</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full mt-1 justify-start text-sm">
              {dateVal ? format(dateVal, 'yyyy-MM-dd') : 'เลือกวันที่'}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="p-0">
            <Calendar mode="single" selected={dateVal} onSelect={setDateVal as any} />
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <Label>เวลาเริ่ม</Label>
        <Select value={startVal} onValueChange={setStartVal}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="เลือก" /></SelectTrigger>
          <SelectContent>
            {timeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>เวลาสิ้นสุด</Label>
        <Select value={endVal} onValueChange={setEndVal}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="เลือก" /></SelectTrigger>
          <SelectContent>
            {timeOptions.filter((t) => !startVal || t > startVal).map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>แก้ไขข้อมูลคลาส</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Edit category */}
          <div>
            <Label>ประเภทการแก้ไข</Label>
            <Select value={editCategory} onValueChange={(v) => { setEditCategory(v as any); setAdHocReason(''); setPermanentSubType(''); }}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="เลือกประเภท" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AD_HOC">สร้างตารางเฉพาะกิจ</SelectItem>
                <SelectItem value="PERMANENT">แก้ไขตารางประจำ</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* AD_HOC form */}
          {editCategory === 'AD_HOC' && (
            <div className="space-y-3 border rounded-md p-3">
              <div>
                <Label>เหตุผลในการสร้างตารางเฉพาะกิจ</Label>
                <Select value={adHocReason} onValueChange={setAdHocReason}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="เลือกเหตุผล" /></SelectTrigger>
                  <SelectContent>
                    {AD_HOC_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {renderTimeSelectors(adHocStart, setAdHocStart, adHocEnd, setAdHocEnd, adHocDate, setAdHocDate)}
              <div>
                <Label>วิชาที่สอน</Label>
                <Input className="mt-1" value={adHocSubject} onChange={(e) => setAdHocSubject(e.target.value)} placeholder={defaultSubject || 'วิชา'} />
              </div>
            </div>
          )}

          {/* PERMANENT form */}
          {editCategory === 'PERMANENT' && (
            <div className="space-y-3 border rounded-md p-3">
              <div>
                <Label>หัวข้อการแก้ไข</Label>
                <Select value={permanentSubType} onValueChange={setPermanentSubType}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="เลือกหัวข้อ" /></SelectTrigger>
                  <SelectContent>
                    {PERMANENT_SUB_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {permanentSubType === 'CHANGE_TIME' && renderTimeSelectors(permStart, setPermStart, permEnd, setPermEnd, permDate, setPermDate)}

              {permanentSubType === 'CHANGE_SUBJECT' && (
                <div className="space-y-3">
                  <div>
                    <Label>วิชา</Label>
                    <Input className="mt-1" value={permSubject} onChange={(e) => setPermSubject(e.target.value)} />
                  </div>
                  <div>
                    <Label>ชั้นปี</Label>
                    <Input className="mt-1" value={permGrade} onChange={(e) => setPermGrade(e.target.value)} placeholder="เช่น ม.3, ป.6" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="special-tutoring" checked={isSpecialTutoring} onCheckedChange={(v) => setIsSpecialTutoring(!!v)} />
                    <Label htmlFor="special-tutoring" className="cursor-pointer">เป็นการติวพิเศษ</Label>
                  </div>
                </div>
              )}

              {permanentSubType === 'CANCEL_PERMANENT' && (
                <p className="text-sm text-muted-foreground">คำขอยกเลิกตารางเรียนนี้อย่างถาวร จะถูกส่งให้ admin พิจารณา</p>
              )}
            </div>
          )}

          {/* Note (always shown) */}
          <div>
            <Label>อธิบายเพิ่มเติม</Label>
            <Textarea className="mt-1" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="อธิบายเหตุผลเพิ่มเติม..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>ยกเลิก</Button>
          <Button onClick={handleSubmit} disabled={submitting || !editCategory}>ส่งคำขอ</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={showSuccess} onOpenChange={(v) => { setShowSuccess(v); if (!v) onSuccess?.(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex justify-center mb-2">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          </div>
          <AlertDialogTitle className="text-center">ได้รับคำขอแก้ไขข้อมูลคลาสแล้ว</AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            โปรดรอการ approve จากทีมงาน
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center">
          <AlertDialogAction onClick={() => { setShowSuccess(false); onSuccess?.(); }}>ตกลง</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
